import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import WaveSurfer from "wavesurfer.js";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * AudioPlayer — wavesurfer.js wrapper.
 * Dizayn yo'riqnoma § 2.8.
 *
 * Variantlar: compact (64px) / expanded (128px waveform)
 * Keyboard: Space (play/pause), ←→ (±5s), ↑↓ (volume), M (mute)
 *
 * Imperative API (ref orqali):
 *   - seekTo(sec) — vaqtga ko'chirish
 *   - play() / pause() / togglePlay()
 */

export interface AudioPlayerHandle {
  seekTo: (sec: number) => void;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  getCurrentTime: () => number;
}

interface AudioPlayerProps {
  src: string;
  /** Backend taqdim etgan davomiyligi (ready bo'lgunga qadar UI uchun fallback) */
  duration?: number;
  variant?: "compact" | "expanded";
  /** Vaqt diapazonida marker chiziqlar (segment boshlari) */
  segmentMarkers?: number[];
  onSeek?: (timeSec: number) => void;
  onTimeUpdate?: (timeSec: number) => void;
  onReady?: () => void;
  className?: string;
}

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

/**
 * CSS variable'dan hex/rgb qiymatni o'qish.
 * wavesurfer.js canvas API va boshqa hex-only API'lar uchun.
 */
function getCssVarHex(name: string, fallback: string): string {
  if (typeof window === "undefined" || typeof document === "undefined") return fallback;
  const value = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
}

function fmtTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

export const AudioPlayer = forwardRef<AudioPlayerHandle, AudioPlayerProps>(function AudioPlayer(
  {
    src,
    duration: durationProp,
    variant = "compact",
    segmentMarkers,
    onSeek,
    onTimeUpdate,
    onReady,
    className,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.9);
  const [speed, setSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(durationProp ?? 0);
  const [ready, setReady] = useState(false);

  // wavesurfer.js init
  useEffect(() => {
    if (!containerRef.current) return;

    const ws = WaveSurfer.create({
      container: containerRef.current,
      // wavesurfer.js canvas API, var(...) qabul qilmaydi — fallback hex.
      // Theme o'zgarsa, react re-mount qiladigan boshqa joy emas, qabul.
      waveColor: getCssVarHex("--border-strong", "#33333F"),
      progressColor: getCssVarHex("--brand-primary", "#8B5CF6"),
      cursorColor: getCssVarHex("--brand-peach", "#4F8FFF"),
      barWidth: 2,
      barGap: 1,
      barRadius: 1,
      height: variant === "expanded" ? 96 : 40,
      normalize: true,
      url: src,
    });

    wsRef.current = ws;

    ws.on("ready", () => {
      setReady(true);
      setTotalDuration(ws.getDuration());
      onReady?.();
    });
    ws.on("play", () => setPlaying(true));
    ws.on("pause", () => setPlaying(false));
    ws.on("finish", () => setPlaying(false));
    ws.on("audioprocess", () => {
      const t = ws.getCurrentTime();
      setCurrentTime(t);
      onTimeUpdate?.(t);
    });
    ws.on("seeking", (t) => {
      setCurrentTime(t);
      onTimeUpdate?.(t);
    });

    return () => {
      try {
        ws.destroy();
      } catch {
        // noop
      }
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, variant]);

  // Volume / mute / speed sync
  useEffect(() => {
    wsRef.current?.setVolume(muted ? 0 : volume);
  }, [muted, volume]);

  useEffect(() => {
    wsRef.current?.setPlaybackRate(speed, true);
  }, [speed]);

  const togglePlay = useCallback(() => {
    if (!ready || !wsRef.current) return;
    wsRef.current.playPause();
  }, [ready]);

  const seekDelta = useCallback(
    (deltaSec: number) => {
      if (!wsRef.current || !ready) return;
      const total = wsRef.current.getDuration();
      const next = Math.max(0, Math.min(total, wsRef.current.getCurrentTime() + deltaSec));
      wsRef.current.setTime(next);
      onSeek?.(next);
    },
    [ready, onSeek],
  );

  const seekTo = useCallback(
    (sec: number) => {
      if (!wsRef.current || !ready) return;
      const total = wsRef.current.getDuration();
      const next = Math.max(0, Math.min(total, sec));
      wsRef.current.setTime(next);
      onSeek?.(next);
    },
    [ready, onSeek],
  );

  // Imperative API (parent ref orqali audio'ni boshqarish — masalan SpeakerTurn'ga klik)
  useImperativeHandle(
    ref,
    () => ({
      seekTo,
      play: () => wsRef.current?.play(),
      pause: () => wsRef.current?.pause(),
      togglePlay: () => wsRef.current?.playPause(),
      getCurrentTime: () => wsRef.current?.getCurrentTime() ?? 0,
    }),
    [seekTo],
  );

  // Keyboard
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Faqat container fokusda yoki tashqi inputlardan tashqari bosilganda
      const target = e.target as HTMLElement;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
      if (e.code === "Space") {
        e.preventDefault();
        togglePlay();
      } else if (e.code === "ArrowLeft") {
        seekDelta(-5);
      } else if (e.code === "ArrowRight") {
        seekDelta(5);
      } else if (e.code === "ArrowUp") {
        setVolume((v) => Math.min(1, v + 0.1));
      } else if (e.code === "ArrowDown") {
        setVolume((v) => Math.max(0, v - 0.1));
      } else if (e.code === "KeyM") {
        setMuted((m) => !m);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [togglePlay, seekDelta]);

  return (
    <div
      className={cn(
        "rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-3",
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label={playing ? "Pauza" : "Ijro etish"}
          onClick={togglePlay}
          disabled={!ready}
          className={cn(
            "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
            "bg-[var(--brand-primary)] text-[var(--text-on-brand)] shadow-md transition-transform hover:scale-105",
            "disabled:opacity-50",
          )}
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-0.5" />}
        </button>

        <div className="relative flex-1 min-w-0">
          <div ref={containerRef} aria-label="Audio waveform" />
          {/* Segment markers */}
          {variant === "expanded" && segmentMarkers && totalDuration > 0 && (
            <div className="pointer-events-none absolute inset-0">
              {segmentMarkers.map((sec, i) => (
                <span
                  key={i}
                  className="absolute top-0 bottom-0 w-px bg-[rgba(139,92,246,0.32)]"
                  style={{ left: `${(sec / totalDuration) * 100}%` }}
                  aria-hidden
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <span className="font-mono text-xs tabular-nums text-[var(--text-muted)]">
            {fmtTime(currentTime)} / {fmtTime(totalDuration)}
          </span>

          <select
            aria-label="Tezlik"
            className="rounded-md border border-[var(--border-subtle)] bg-transparent px-1.5 py-0.5 text-xs text-[var(--text-secondary)] outline-none"
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value))}
          >
            {SPEEDS.map((s) => (
              <option key={s} value={s}>
                {s}x
              </option>
            ))}
          </select>

          <button
            type="button"
            aria-label={muted ? "Ovozni yoqish" : "Ovozni o'chirish"}
            onClick={() => setMuted((m) => !m)}
            className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
});
